/**
 * Student/parent misc native screens — school blog, notification pages, news
 * approval OTP, "Upgrade to Pro", parenting welcome/contact and the child
 * profile extras. API paths are literals here on purpose: the endpoints are
 * private to these screens.
 *
 * Every creator takes its own arguments, then `onSuccess` / `onFailure`, so
 * it can be driven by `useParentingResource`. The saga adds a parent's
 * selected child automatically (`?childId=` on GET, `childId` in the body
 * otherwise).
 */
import {
  SP_MISC_GET_CHILD_PROFILE,
  SP_MISC_GET_NEWS_APPROVAL_OTP,
  SP_MISC_GET_NOTIFICATION_DETAIL,
  SP_MISC_GET_OWN_PROFILE,
  SP_MISC_GET_PARENTING_WELCOME,
  SP_MISC_GET_PRO_UPGRADE,
  SP_MISC_GET_SCHOOL_BLOG_POST,
  SP_MISC_GET_SCHOOL_BLOG_POSTS,
  SP_MISC_POST_SCHOOL_BLOG_COMMENT,
  SP_MISC_SEND_NEWS_APPROVAL_OTP,
  SP_MISC_SEND_PARENTING_CONTACT,
  SP_MISC_VERIFY_NEWS_APPROVAL_OTP,
} from './ActionTypes/ApiActionTypes'

/**
 * @typedef {(response: any) => void} Callback
 */

/**
 * Builds a `fetchData` request action.
 * @param {string} type saga action type
 * @param {string} apiUrl path under the API base url
 * @param {{requestType?: 'GET'|'POST', body?: object, query?: Record<string, string|number|undefined|null>}} options
 * @param {Callback} [onSuccess]
 * @param {Callback} [onFailure]
 */
const request = (type, apiUrl, {requestType = 'GET', body, query} = {}, onSuccess, onFailure) => {
  const params = new URLSearchParams()
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.append(key, String(value))
  })
  const getString = params.toString() ? `?${params.toString()}` : ''

  return {
    type,
    payload: {
      apiUrl,
      getString,
      useBaseURL: true,
      requestType,
      header: 'application/json',
      body: requestType === 'GET' ? undefined : (body ?? {}),
      reduxActionType: '',
      onSuccess,
      onFailure,
    },
  }
}

// ------------------------------------------------------------ school blog

/**
 * One page of published school blog posts plus the sidebar.
 * @param {{page?: number, categoryId?: string|number, year?: string|number, month?: string|number}} filters
 * @param {Callback} [onSuccess]
 * @param {Callback} [onFailure]
 */
export const getSchoolBlogPosts = (filters, onSuccess, onFailure) =>
  request(
    SP_MISC_GET_SCHOOL_BLOG_POSTS,
    'school-blog',
    {query: filters ?? {}},
    onSuccess,
    onFailure
  )

/** @param {number|string} id @param {Callback} [onSuccess] @param {Callback} [onFailure] */
export const getSchoolBlogPost = (id, onSuccess, onFailure) =>
  request(SP_MISC_GET_SCHOOL_BLOG_POST, `school-blog/${id}`, {}, onSuccess, onFailure)

/**
 * Leaves a comment (held for approval).
 * @param {number|string} id post id
 * @param {string} comment
 * @param {Callback} [onSuccess]
 * @param {Callback} [onFailure]
 */
export const postSchoolBlogComment = (id, comment, onSuccess, onFailure) =>
  request(
    SP_MISC_POST_SCHOOL_BLOG_COMMENT,
    `school-blog/${id}/comments`,
    {requestType: 'POST', body: {comment}},
    onSuccess,
    onFailure
  )

// ---------------------------------------------------------- notifications

/** @param {number|string} id @param {Callback} [onSuccess] @param {Callback} [onFailure] */
export const getNotificationDetail = (id, onSuccess, onFailure) =>
  request(SP_MISC_GET_NOTIFICATION_DETAIL, `user-notifications/${id}`, {}, onSuccess, onFailure)

// ------------------------------------------------------- news approval OTP

/** @param {number|string} id news id @param {Callback} [onSuccess] @param {Callback} [onFailure] */
export const getNewsApprovalOtp = (id, onSuccess, onFailure) =>
  request(SP_MISC_GET_NEWS_APPROVAL_OTP, `news/${id}/approval-otp`, {}, onSuccess, onFailure)

/** @param {number|string} id news id @param {Callback} [onSuccess] @param {Callback} [onFailure] */
export const sendNewsApprovalOtp = (id, onSuccess, onFailure) =>
  request(
    SP_MISC_SEND_NEWS_APPROVAL_OTP,
    `news/${id}/approval-otp`,
    {requestType: 'POST'},
    onSuccess,
    onFailure
  )

/**
 * @param {number|string} id news id
 * @param {string} otp the code the user typed
 * @param {Callback} [onSuccess]
 * @param {Callback} [onFailure]
 */
export const verifyNewsApprovalOtp = (id, otp, onSuccess, onFailure) =>
  request(
    SP_MISC_VERIFY_NEWS_APPROVAL_OTP,
    `news/${id}/approval-otp/verify`,
    {requestType: 'POST', body: {otp}},
    onSuccess,
    onFailure
  )

// ------------------------------------------------------------ pro upgrade

/** @param {Callback} [onSuccess] @param {Callback} [onFailure] */
export const getProUpgrade = (onSuccess, onFailure) =>
  request(SP_MISC_GET_PRO_UPGRADE, 'pro-upgrade', {}, onSuccess, onFailure)

// -------------------------------------------------------------- parenting

/** @param {Callback} [onSuccess] @param {Callback} [onFailure] */
export const getParentingWelcome = (onSuccess, onFailure) =>
  request(SP_MISC_GET_PARENTING_WELCOME, 'parenting/welcome', {}, onSuccess, onFailure)

/**
 * @param {{name: string, email: string, phone: string, body: string}} body
 * @param {Callback} [onSuccess]
 * @param {Callback} [onFailure]
 */
export const sendParentingContact = (body, onSuccess, onFailure) =>
  request(
    SP_MISC_SEND_PARENTING_CONTACT,
    'parenting/contact',
    {requestType: 'POST', body},
    onSuccess,
    onFailure
  )

// ---------------------------------------------------------- child profile

/**
 * A parent's selected child — the mobile app's `POST user/child-profile`
 * (the saga adds `childId`).
 * @param {Callback} [onSuccess]
 * @param {Callback} [onFailure]
 */
export const getChildProfileDetails = (onSuccess, onFailure) =>
  request(
    SP_MISC_GET_CHILD_PROFILE,
    'user/child-profile',
    {requestType: 'POST'},
    onSuccess,
    onFailure
  )

/**
 * A student's own profile — the mobile app's `GET user/profile-details`.
 * @param {Callback} [onSuccess]
 * @param {Callback} [onFailure]
 */
export const getOwnProfileDetails = (onSuccess, onFailure) =>
  request(SP_MISC_GET_OWN_PROFILE, 'user/profile-details', {}, onSuccess, onFailure)
